/*
Copyright 2026

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only OR LicenseRef-Element-Commercial
*/

import { createClient, type SupabaseClient, type Session } from "@supabase/supabase-js";

import SdkConfig from "../SdkConfig";

export type HubMatrixCredentials = {
    access_token: string;
    device_id: string;
    user_id: string;
    hs_url: string;
};

export type HubSupabaseSession = {
    access_token: string;
    refresh_token: string;
    expires_at?: number;
};

export type HubClientLoginResponse = {
    matrix: HubMatrixCredentials;
    supabase?: HubSupabaseSession;
};

export type HubStaffPasswordStateResponse = {
    password_state: string;
};

export type HubClientSignupPayload = {
    user_local_id: string;
    company_name: string;
    password: string;
    display_name?: string;
    gender?: string;
    job_title?: string;
};

export type PendingClientRegistration = {
    user_local_id: string;
    email: string;
    company_name: string;
    gender?: string;
    job_title?: string;
    created_at: string;
};

type SupabaseCallbackPayload = {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    token_type?: string;
    type?: string;
};

let cachedSupabaseClient: SupabaseClient | null = null;
let cachedSupabaseConfig: { url: string; anonKey: string } | null = null;
const SUPABASE_CALLBACK_STORAGE_KEY = "gt_supabase_callback";
const PENDING_CLIENT_REGISTRATION_STORAGE_KEY = "gt_pending_client_registration";

function normalizeBaseUrl(value: string): string {
    return value.replace(/\/+$/, "");
}

export function getHubApiBaseUrl(): string {
    const hubBaseUrl = SdkConfig.get("hub_api_base_url");
    if (!hubBaseUrl) {
        throw new Error("Missing hub_api_base_url");
    }
    return normalizeBaseUrl(hubBaseUrl);
}

export function getSupabaseClient(): SupabaseClient {
    const supabaseUrl = SdkConfig.get("supabase_url");
    const supabaseAnonKey = SdkConfig.get("supabase_anon_key");
    if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error("Missing supabase_url or supabase_anon_key");
    }

    if (
        !cachedSupabaseClient ||
        !cachedSupabaseConfig ||
        cachedSupabaseConfig.url !== supabaseUrl ||
        cachedSupabaseConfig.anonKey !== supabaseAnonKey
    ) {
        cachedSupabaseConfig = {
            url: supabaseUrl,
            anonKey: supabaseAnonKey,
        };
        cachedSupabaseClient = createClient(supabaseUrl, supabaseAnonKey);
    }

    return cachedSupabaseClient;
}

export async function hubClientLogin(account: string, password: string): Promise<HubClientLoginResponse> {
    const hubBaseUrl = getHubApiBaseUrl();
    return postJson<HubClientLoginResponse>(`${hubBaseUrl}/client/login`, {
        account,
        password,
    });
}

export async function hubClientProvision(
    accessToken: string,
    payload: HubClientSignupPayload,
): Promise<{ matrix: HubMatrixCredentials }> {
    const hubBaseUrl = getHubApiBaseUrl();
    return postJson<{ matrix: HubMatrixCredentials }>(
        `${hubBaseUrl}/client/signup-provision`,
        payload,
        accessToken,
    );
}

export async function hubClientResetPassword(accessToken: string, password: string): Promise<void> {
    const hubBaseUrl = getHubApiBaseUrl();
    await postJson<Record<string, unknown>>(
        `${hubBaseUrl}/client/reset-password`,
        { password },
        accessToken,
    );
}

export async function hubStaffPasswordState(
    accessToken: string,
    hsUrl: string,
): Promise<HubStaffPasswordStateResponse> {
    const hubBaseUrl = getHubApiBaseUrl();
    const url = new URL(`${hubBaseUrl}/staff/password-state/self`);
    url.searchParams.set("hs_url", hsUrl);
    return getJson<HubStaffPasswordStateResponse>(url.toString(), accessToken);
}

export async function hubStaffActivatePasswordState(accessToken: string, hsUrl: string): Promise<void> {
    const hubBaseUrl = getHubApiBaseUrl();
    await postJson<Record<string, unknown>>(
        `${hubBaseUrl}/staff/password-state/activate-self`,
        { hs_url: hsUrl },
        accessToken,
    );
}

export function toHubSession(session: Session | null | undefined): HubSupabaseSession | null {
    if (!session) return null;
    return {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at,
    };
}

export function storePendingClientRegistration(data: PendingClientRegistration): void {
    localStorage.setItem(PENDING_CLIENT_REGISTRATION_STORAGE_KEY, JSON.stringify(data));
}

export function loadPendingClientRegistration(): PendingClientRegistration | null {
    const raw = localStorage.getItem(PENDING_CLIENT_REGISTRATION_STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as PendingClientRegistration;
    } catch {
        return null;
    }
}

export function clearPendingClientRegistration(): void {
    localStorage.removeItem(PENDING_CLIENT_REGISTRATION_STORAGE_KEY);
}

export function captureSupabaseCallbackFromHash(hash: string): boolean {
    const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!fragment) return false;
    const query = fragment.includes("?") ? fragment.split("?")[1] : fragment;
    if (!query || !query.includes("access_token=")) return false;

    const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    if (!accessToken || !refreshToken) return false;

    const payload: SupabaseCallbackPayload = {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_in: params.get("expires_in") ? Number(params.get("expires_in")) : undefined,
        token_type: params.get("token_type") ?? undefined,
        type: params.get("type") ?? undefined,
    };

    sessionStorage.setItem(SUPABASE_CALLBACK_STORAGE_KEY, JSON.stringify(payload));
    return true;
}

export function getSupabaseCallbackPayload(): SupabaseCallbackPayload | null {
    const raw = sessionStorage.getItem(SUPABASE_CALLBACK_STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as SupabaseCallbackPayload;
    } catch {
        return null;
    }
}

export function clearSupabaseCallbackPayload(): void {
    sessionStorage.removeItem(SUPABASE_CALLBACK_STORAGE_KEY);
}

export async function setSupabaseSessionFromCallback(): Promise<Session | null> {
    const payload = getSupabaseCallbackPayload();
    if (!payload) return null;
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.setSession({
        access_token: payload.access_token,
        refresh_token: payload.refresh_token,
    });
    if (error) {
        throw new Error(error.message);
    }
    return data.session;
}

async function postJson<T>(
    url: string,
    body: Record<string, unknown>,
    accessToken?: string,
): Promise<T> {
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(await readResponseMessage(response));
    }

    return (await response.json()) as T;
}

async function getJson<T>(url: string, accessToken?: string): Promise<T> {
    const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(await readResponseMessage(response));
    }

    return (await response.json()) as T;
}

async function readResponseMessage(response: Response): Promise<string> {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
        try {
            const data = (await response.json()) as { message?: string };
            if (data?.message) return data.message;
        } catch {
            // fall through
        }
    }

    const text = await response.text();
    return text || `Request failed (${response.status})`;
}
